import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
    Bold, Italic, Underline as UnderlineIcon, Strikethrough,
    Highlighter, Code, Palette
} from 'lucide-react';
import { TEXT_COLORS, HIGHLIGHT_COLORS } from './EditorConfig';

const ColorSwatches = ({ onSelect, colors = TEXT_COLORS }) => (
    <div className="tiptap-color-picker">
        {colors.map(c => (
            <button
                key={c.label}
                className="btn btn-ghost btn-sm tiptap-color-picker__swatch"
                title={c.label}
                style={{ background: c.value || 'var(--text-primary)', border: c.value ? 'none' : 'var(--border-width-default) dashed var(--border-default)' }}
                onMouseDown={(e) => { e.preventDefault(); onSelect(c.value); }}
            />
        ))}
    </div>
);

export default function FloatingToolbar({ editor, position, onClose }) {
    const [colorPickerOpen, setColorPickerOpen] = useState(false);
    const [hlPickerOpen, setHlPickerOpen] = useState(false);

    const colorBtnRef = useRef(null);
    const hlBtnRef = useRef(null);

    const applyColor = useCallback((color) => {
        if (!editor) return;
        if (color === null) {
            editor.chain().focus().unsetColor().run();
        } else {
            editor.chain().focus().setColor(color).run();
        }
        setColorPickerOpen(false);
        if (onClose) onClose();
    }, [editor, onClose]);

    const applyHighlight = useCallback((color) => {
        if (!editor) return;
        if (color === null) {
            editor.chain().focus().unsetHighlight().run();
        } else {
            editor.chain().focus().setHighlight({ color }).run();
        }
        setHlPickerOpen(false);
        if (onClose) onClose();
    }, [editor, onClose]);

    // Close pickers on outside click
    useEffect(() => {
        const handleClick = (e) => {
            if (colorBtnRef.current && !colorBtnRef.current.contains(e.target)) {
                setColorPickerOpen(false);
            }
            if (hlBtnRef.current && !hlBtnRef.current.contains(e.target)) {
                setHlPickerOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    if (!editor || !position) return null;

    return (
        <div
            className="tiptap-bubble"
            style={{ position: 'absolute', top: position.top, left: position.left, zIndex: 50 }}
        >
            <button
                className={`tiptap-bubble__btn ${editor.isActive('bold') ? 'tiptap-bubble__btn--active' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBold().run(); }}
            >
                <Bold size={14} />
            </button>
            <button
                className={`tiptap-bubble__btn ${editor.isActive('italic') ? 'tiptap-bubble__btn--active' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleItalic().run(); }}
            >
                <Italic size={14} />
            </button>
            <button
                className={`tiptap-bubble__btn ${editor.isActive('underline') ? 'tiptap-bubble__btn--active' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleUnderline().run(); }}
            >
                <UnderlineIcon size={14} />
            </button>
            <button
                className={`tiptap-bubble__btn ${editor.isActive('strike') ? 'tiptap-bubble__btn--active' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleStrike().run(); }}
            >
                <Strikethrough size={14} />
            </button>
            <div className="tiptap-bubble__divider" />

            <div ref={hlBtnRef} style={{ position: 'relative' }}>
                <button
                    className={`tiptap-bubble__btn tiptap-bubble__btn--hl ${editor.isActive('highlight') ? 'tiptap-bubble__btn--active' : ''}`}
                    onMouseDown={(e) => { e.preventDefault(); setHlPickerOpen(o => !o); }}
                >
                    <Highlighter size={14} />
                </button>
                {hlPickerOpen && (
                    <div className="tiptap-color-dropdown">
                        <ColorSwatches onSelect={applyHighlight} colors={HIGHLIGHT_COLORS} />
                    </div>
                )}
            </div>

            <button
                className={`tiptap-bubble__btn ${editor.isActive('code') ? 'tiptap-bubble__btn--active' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleCode().run(); }}
            >
                <Code size={14} />
            </button>
            <div className="tiptap-bubble__divider" />
            
            {/* Color picker in floating toolbar */}
            <div ref={colorBtnRef} style={{ position: 'relative' }}>
                <button
                    className="tiptap-bubble__btn"
                    onMouseDown={(e) => { e.preventDefault(); setColorPickerOpen(o => !o); }}
                    title="Text Color"
                >
                    <Palette size={14} />
                </button>
                {colorPickerOpen && (
                    <div className="tiptap-color-dropdown">
                        <ColorSwatches onSelect={applyColor} />
                    </div>
                )}
            </div>
        </div>
    );
}
